import { AuthServiceInterface } from '@services/AuthService/interface';
import {
  BitrixUser,
  FavoriteProduct,
  HistoryItem,
  LoadBitrixUserQuery,
  LoadUserQueryResponse,
} from '@services/AuthService/query/loadUserQuery';
import { $error } from '@settings/errorContext';
import { graphQLClient } from '@settings/services/graphQLClient';
import { GraphQLClient } from '@settings/services/graphQLClient/GraphQLClient';
import { loggerFactory } from '@settings/services/logger';

import {
  AddFavoritesProductMutation,
  AddFavoritesProductMutationParams,
  AddFavoritesProductMutationResponse,
} from './mutations/AddFavoritesProductMutation';
import {
  DeleteArchivedProductMutation,
  DeleteArchivedProductMutationParams,
} from './mutations/DeleteArchivedProductMutation';
import {
  CreateIndividualOfferMutation,
  CreateIndividualOfferMutationResponse,
  CreateIndividualOfferMutationVars,
  CreateIndividualOfferParams,
  CreateIndividualOfferResult,
} from './mutations/CreateIndividualOfferMutation';
import {
  DeleteFavoritesProductMutation,
  DeleteFavoritesProductMutationParams,
  DeleteFavoritesProductMutationResponse,
} from './mutations/DeleteFavoritesProductMutation';
import {
  DeleteIndividualOfferQuery,
  DeleteIndividualOfferQueryParams,
  DeleteIndividualOfferQueryResponse,
} from './query/deleteIndividualOfferQuery';
import {
  LoadIndividualOffersValidDatesQuery,
  LoadIndividualOffersValidDatesQueryParams,
  LoadIndividualOffersValidDatesQueryResponse,
  PreOrderValidDate,
} from './query/loadIndividualOffersValidDatesQuery';
import {
  LoadBitrixUserHistoryQuery,
  LoadBitrixUserHistoryQueryResponse,
} from './query/loadUserHistoryQuery';

class AuthService implements AuthServiceInterface {
  private readonly logger = loggerFactory().make(`AuthService`);
  private readonly client: GraphQLClient;

  constructor(token?: string) {
    this.client = graphQLClient(token);
  }

  async LoadUser(token?: string): Promise<BitrixUser | null> {
    if (!token) {
      return null;
    }

    try {
      const { BitrixUserProfile } = await this.client.Query<null, LoadUserQueryResponse>(
        new LoadBitrixUserQuery(),
        {}
      );

      this.logger.Debug(`Load bitrix user`, BitrixUserProfile);

      return BitrixUserProfile;
    } catch (e) {
      this.logger.Error(e);

      throw e;
    }
  }

  async LoadUserHistory(token?: string): Promise<HistoryItem[] | null> {
    if (!token) {
      return null;
    }

    try {
      const { BitrixUserProfile } = await this.client.Query<
        null,
        LoadBitrixUserHistoryQueryResponse
      >(new LoadBitrixUserHistoryQuery(), {});

      this.logger.Debug(`Load bitrix user history`, BitrixUserProfile?.history);

      return BitrixUserProfile?.history ?? null;
    } catch (e) {
      this.logger.Error(e);

      throw e;
    }
  }

  async LoadIndividualOffersValidDates(preOrderIds: string[]): Promise<PreOrderValidDate[]> {
    if (!preOrderIds.length) {
      return [];
    }

    try {
      const { items } = await this.client.Query<
        LoadIndividualOffersValidDatesQueryParams,
        LoadIndividualOffersValidDatesQueryResponse
      >(new LoadIndividualOffersValidDatesQuery(preOrderIds), {});

      this.logger.Debug(`Load individual offers valid dates`, items);

      return items ?? [];
    } catch (e) {
      this.logger.Error(e);

      throw e;
    }
  }

  async CreateIndividualOffer(
    params: CreateIndividualOfferParams
  ): Promise<CreateIndividualOfferResult> {
    try {
      const response = await this.client.Query<
        CreateIndividualOfferMutationVars,
        CreateIndividualOfferMutationResponse
      >(new CreateIndividualOfferMutation(params), {});

      this.logger.Debug(`Individual offer created`, response.data);

      return response.data?.result ? 'created' : 'notFound';
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);

      return 'failed';
    }
  }

  async DeleteIndividualOffer(params: DeleteIndividualOfferQueryParams): Promise<boolean> {
    try {
      const response = await this.client.Query<
        DeleteIndividualOfferQueryParams,
        DeleteIndividualOfferQueryResponse
      >(new DeleteIndividualOfferQuery(params), {});

      this.logger.Debug(`Individual offer deleted`, response.data);

      return !!response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);

      return false;
    }
  }

  async AddFavoritesProduct(params: AddFavoritesProductMutationParams): Promise<FavoriteProduct> {
    try {
      const response = await this.client.Query<
        AddFavoritesProductMutationParams,
        AddFavoritesProductMutationResponse
      >(new AddFavoritesProductMutation(params), {});

      this.logger.Debug(`Added product to favorites`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }

  async DeleteFavoritesProduct(params: DeleteFavoritesProductMutationParams): Promise<boolean> {
    try {
      const response = await this.client.Query<
        DeleteFavoritesProductMutationParams,
        DeleteFavoritesProductMutationResponse
      >(new DeleteFavoritesProductMutation(params), {});

      this.logger.Debug(`Favorite product deleted`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }

  async DeleteArchivedProduct(params: DeleteArchivedProductMutationParams): Promise<boolean> {
    try {
      const response = await this.client.Query<
        DeleteArchivedProductMutationParams,
        DeleteFavoritesProductMutationResponse
      >(new DeleteArchivedProductMutation(params), {});

      this.logger.Debug(`Archived product deleted`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }
}

export default AuthService;
